import React from 'react';
import PropTypes from 'prop-types';
import SVGIcon from '../SVGIcon/SVGIcon';
import { ListItemContainer, ListItemIcon } from './ListItem.style';
import Button from '../Button/Button';

/**
 * Use inside the <a href="/docs/components-list--standard">List</a> component.
 *
 * A ListItem has a default bullet icon, but the icon and its size and color can be adapted via the props.
 *
 * Optionally a click handler can be passed, which will fire on the entire list item.
 *
 */

const renderBulletOrIcon = function (icon, iconOptions) {
  if (icon) return <SVGIcon icon={icon} {...iconOptions} tw="text-gray-lighten-30" />;
  return null;
};

const renderIconOrIconButton = function (icon, useIconAsButton, onClick, iconOptions) {
  if (useIconAsButton && onClick) {
    return (
      <Button
        tw="p-0 mr-4 my-2 text-gray-lighten-30 flex flex-shrink-0"
        contentType="icon"
        size="small"
        icon={icon}
        onClick={onClick}
      />
    );
  }
  return <ListItemIcon>{renderBulletOrIcon(icon, iconOptions)}</ListItemIcon>;
};

const ListItem = ({
  children,
  onClick = false,
  icon = 'bullet',
  iconOptions = {
    size: 'medium'
  },
  className = '',
  useIconAsButton = false
}) => {
  let onClickProps = {};
  if (onClick) {
    onClickProps = { onClick, role: 'button' };
    if (!useIconAsButton) {
      /* Passing on tabIndex will make the element focusable when navigating with the keyboard.
        When using the icon as button that button will be focusable instead  */
      onClickProps.tabIndex = 0;
    }
  }

  return (
    <ListItemContainer role="listitem" className={className} {...onClickProps}>
      {icon && renderIconOrIconButton(icon, useIconAsButton, onClick, iconOptions)}

      {children}
    </ListItemContainer>
  );
};

ListItem.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  iconOptions: PropTypes.shape({
    size: PropTypes.string
  }),
  useIconAsButton: PropTypes.bool,
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string
};

export default ListItem;
